import { ReactNode } from 'react';
import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatDisplayValue } from '@/lib/formatNumber';

interface TrendData {
  value: number;
  direction: 'up' | 'down';
}

interface KPICardProps {
  label: string;
  value: string | number;
  icon?: LucideIcon;
  trend?: TrendData;
  secondaryValue?: string;
  isLoading?: boolean;
  className?: string;
  onClick?: () => void;
  children?: ReactNode;
}

export function KPICard({
  label,
  value,
  icon: Icon,
  trend,
  secondaryValue,
  isLoading = false,
  className,
  onClick,
  children,
}: KPICardProps) {
  const TrendIcon = trend?.direction === 'up' ? TrendingUp : TrendingDown;
  const trendColorClass = trend?.direction === 'up' ? 'text-green-600' : 'text-red-600';
  const isClickable = !!onClick;

  return (
    <Card
      className={cn(
        'relative overflow-hidden transition-all',
        isClickable && 'cursor-pointer hover:shadow-md hover:border-primary/50',
        className
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {label}
            </span>
            {isLoading ? (
              <div className="h-8 w-20 animate-pulse rounded bg-muted" />
            ) : (
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold tracking-tight tabular-nums">
                  {formatDisplayValue(value)}
                </span>
                {secondaryValue && (
                  <span className="text-sm text-muted-foreground">
                    {secondaryValue}
                  </span>
                )}
              </div>
            )}
            {children && !isLoading && children}
            {trend && !isLoading && !children && (
              <div className={cn('flex items-center gap-1 text-xs font-medium', trendColorClass)}>
                <TrendIcon className="h-3 w-3" />
                <span>{Math.abs(trend.value)}%</span>
              </div>
            )}
          </div>
          {Icon && (
            <div className="rounded-lg bg-muted p-2">
              <Icon className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default KPICard;
